import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon
} from '@mui/icons-material';
import axiosInstance from '../../utils/axiosConfig';

interface Transaction {
  id: string;
  sequentialId: number;
  date: string;
  referenceNumber: string;
  referenceType: string;
  description: string;
  debit: number;
  credit: number;
  balance: number;
  notes: string;
  branchName?: string; // إضافة اسم الفرع
}

interface ContactInfo {
  id: string;
  name: string;
  type: string;
}

interface ContactTransactionsProps {
  contactId: string;
}

const ContactTransactions: React.FC<ContactTransactionsProps> = ({ contactId }) => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [contactInfo, setContactInfo] = useState<ContactInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totals, setTotals] = useState({ totalDebit: 0, totalCredit: 0 });
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    fetchTransactions();
  }, [contactId, page, rowsPerPage, startDate, endDate]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {
        page: page + 1,
        limit: rowsPerPage
      };

      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await axiosInstance.get(`/contacts/${contactId}/transactions`, { params });

      setTransactions(response.data.transactions || []);
      setContactInfo(response.data.contact);
      setTotalCount(response.data.totalCount || 0);
      setTotals(response.data.totals || { totalDebit: 0, totalCredit: 0 });
    } catch (err: any) {
      console.error('Error fetching transactions:', err);
      setError(err.response?.data?.error || 'حدث خطأ أثناء جلب البيانات');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const getTransactionTypeColor = (type: string) => {
    const typeColors: { [key: string]: string } = {
      'فاتورة مبيعات': 'success',
      'فاتورة مشتريات': 'error',
      'سند قبض': 'info',
      'سند صرف': 'warning',
      'مرتجع مبيعات': 'error',
      'مرتجع مشتريات': 'success',
      'قيد يومية': 'default'
    };
    return typeColors[type] || 'default';
  };

  if (loading && transactions.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!contactInfo) {
    return (
      <Box p={3}>
        <Alert severity="info">لا توجد معلومات متاحة</Alert>
      </Box>
    );
  }

  const netBalance = totals.totalDebit - totals.totalCredit;

  return (
    <Box>
      {/* معلومات العميل/المورد */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
              {contactInfo.name}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {contactInfo.type === 'customer' ? 'عميل' : 'مورد'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box display="flex" justifyContent="flex-end" gap={1}>
              <Chip
                label={contactInfo.type === 'customer' ? 'عميل' : 'مورد'}
                color={contactInfo.type === 'customer' ? 'success' : 'warning'}
                sx={{ fontWeight: 'bold' }}
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* فلاتر التاريخ */}
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              label="من تاريخ"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="إلى تاريخ"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary">
              إجمالي الحركات: {totalCount}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* جدول الحركات */}
      <TableContainer component={Paper} elevation={2}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>رقم الفاتورة</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>التاريخ</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>الفرع</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>نوع الفاتورة</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>الوصف</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>المبلغ الإجمالي</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>الحالة</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Box py={4}>
                    <Typography variant="body1" color="text.secondary">
                      لا توجد فواتير مسجلة
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              transactions.map((transaction) => (
                <TableRow
                  key={transaction.id}
                  hover
                  sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}
                >
                  <TableCell align="center">{transaction.referenceNumber}</TableCell>
                  <TableCell align="center">{formatDate(transaction.date)}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={transaction.branchName || 'غير محدد'}
                      color="primary"
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={transaction.referenceType}
                      color={getTransactionTypeColor(transaction.referenceType) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">{transaction.description}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {formatCurrency((transaction as any).balance || transaction.debit || transaction.credit || 0)}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={(transaction as any).status === 'completed' ? 'مكتملة' : 'مسودة'}
                      color={(transaction as any).status === 'completed' ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="عدد الصفوف:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} من ${count}`}
        />
      </TableContainer>
    </Box>
  );
};

export default ContactTransactions;